// @mentions in message text. People type (or pick) "@Maya"; messages store "<@userId>" (like Discord), so a mention
// keeps pointing at the right person after they change their name, and two people called Maya stay apart.
// Clients show it as "@<current name>". "@everyone" in a channel is stored as "<@everyone>".
// Inside `code` nothing is a mention: it isn't encoded, and a token typed there notifies nobody (as in Discord).
//
// Who encodes: both sides, with the same function.
// - The composer encodes from what was picked in the autocomplete (exact even when names repeat), so the sender's
//   optimistic message already shows the chip and the room usually stores the text unchanged.
// - The room encodes whatever is still typed as "@Name" (no menu used, a pasted message, other clients) from the
//   server's member list, but only where the name points at exactly one member. It also decides what counts:
//   only tokens for current members (and "everyone" where allowed) go into Message.mentions.

/** The id used for "@everyone" in tokens and in Message.mentions. */
export const EVERYONE = "everyone";

/**
 * @Bloom, the AI member of every server. It has no account: "bloom" is a reserved author id (Better Auth ids are
 * 32 characters, so it can't clash). "@Bloom" is stored as "<@bloom>" like any mention, and Bloom's replies are
 * messages whose authorId is BLOOM_ID.
 */
export const BLOOM_ID = "bloom";
export const BLOOM_NAME = "Bloom";

/** A stored mention: "<@" + user id (or "everyone", or "bloom") + ">". */
export const MENTION_TOKEN = /<@([0-9A-Za-z_-]{5,64})>/g;

export interface MentionMember {
  id: string;
  name: string;
}

export interface EncodeOptions {
  /** "@everyone" is allowed here (text channels and their threads, not DMs). When false, a "<@everyone>" token is
   *  turned back into plain text, so nobody can slip one into a DM. */
  everyone: boolean;
  /**
   * People picked from the autocomplete (or already mentioned before an edit), in order. They decide which of
   * several same-named members "@Maya" means; each pick is used once, in text order.
   */
  picked?: MentionMember[];
  /** Typed "@Bloom" means the Bloom agent (default true), even if a member is called Bloom (they can still be
   *  picked from the menu). */
  bloom?: boolean;
}

import { splitCode, withoutCode } from "./markdown.ts";

// "@" at the start or after a space or opening punctuation (so "maya@example.com" doesn't count).
const TYPED_AT = /(^|[\s(\[{"'])@/g;
// A name has to end at a word boundary: "@Mayan" is not "@Maya".
const WORD_CHAR = /[\p{L}\p{N}_]/u;

/** Turns typed "@Name" into "<@id>" for members it can tell apart. Existing tokens are left alone. */
export function encodeMentions(text: string, members: MentionMember[], opts: EncodeOptions): string {
  if (!opts.everyone && text.includes(`<@${EVERYONE}>`)) text = text.replaceAll(`<@${EVERYONE}>`, `@${EVERYONE}`);
  if (!text.includes("@")) return text;

  // Lowercase name → ids. Longest names are tried first, so "@Maya Lopez" beats "@Maya".
  const byName = new Map<string, string[]>();
  for (const m of members) {
    const key = m.name.trim().toLowerCase();
    if (key) byName.set(key, [...(byName.get(key) ?? []), m.id]);
  }
  if (opts.everyone) byName.set(EVERYONE, [EVERYONE]);
  if (opts.bloom !== false) byName.set(BLOOM_NAME.toLowerCase(), [BLOOM_ID]);
  const names = [...byName.keys()].sort((a, b) => b.length - a.length);
  // Picks, per lowercase name, consumed in order.
  const picks = new Map<string, string[]>();
  for (const p of opts.picked ?? []) {
    const key = p.name.trim().toLowerCase();
    picks.set(key, [...(picks.get(key) ?? []), p.id]);
  }

  const encode = (seg: string): string => {
    let out = "";
    let last = 0;
    for (const m of seg.matchAll(TYPED_AT)) {
      const at = m.index! + m[1]!.length;
      if (at < last) continue;
      const rest = seg.slice(at + 1);
      const lower = rest.toLowerCase();
      const name = names.find((n) => lower.startsWith(n) && !WORD_CHAR.test(rest.charAt(n.length)));
      if (!name) continue;
      const ids = byName.get(name)!;
      const id = picks.get(name)?.shift() ?? (ids.length === 1 ? ids[0] : undefined);
      // Two members share this name and nobody picked one: leave the text as typed rather than guess.
      if (!id) continue;
      out += seg.slice(last, at) + `<@${id}>`;
      last = at + 1 + name.length;
    }
    return last ? out + seg.slice(last) : seg;
  };
  // Code stays as typed. Picks are shared across the pieces, so they're still used in text order.
  if (!text.includes("`")) return encode(text);
  return splitCode(text)
    .map((p) => (p.code ? p.s : encode(p.s)))
    .join("");
}

/** The ids in a message's tokens, each once, in order of first appearance ("everyone" included). */
export function mentionIds(body: string): string[] {
  if (!body.includes("<@")) return [];
  return [...new Set([...withoutCode(body).matchAll(MENTION_TOKEN)].map((m) => m[1]!))];
}

/** The people a message's tokens point at, as {id, name} picks, so an edit can re-encode "@Maya" to the same Maya. */
export function mentionPicks(body: string, byId: Map<string, string>): MentionMember[] {
  if (!body.includes("<@")) return [];
  return [...body.matchAll(MENTION_TOKEN)].flatMap((m) => {
    const name = m[1] === EVERYONE ? EVERYONE : m[1] === BLOOM_ID ? BLOOM_NAME : byId.get(m[1]!);
    return name ? [{ id: m[1]!, name }] : [];
  });
}

/** True when the message mentions this person directly or through "@everyone". */
export function mentionsUser(body: string, userId: string | null): boolean {
  if (!userId || !body.includes("<@")) return false;
  const text = withoutCode(body);
  return text.includes(`<@${userId}>`) || text.includes(`<@${EVERYONE}>`);
}

/** Turns "<@id>" back into "@name" (for editing, excerpts and anything shown as plain text). */
export function decodeMentions(text: string, byId: Map<string, string>): string {
  if (!text.includes("<@")) return text;
  return text.replace(MENTION_TOKEN, (_, id: string) => `@${id === EVERYONE ? EVERYONE : id === BLOOM_ID ? BLOOM_NAME : (byId.get(id) ?? "unknown-user")}`);
}

/** True when the message asks @Bloom (a "<@bloom>" token; the room encodes a typed "@Bloom" into one). */
export const mentionsBloom = (body: string) => body.includes(`<@${BLOOM_ID}>`) && withoutCode(body).includes(`<@${BLOOM_ID}>`);
